import { useCallback, useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Link2, UserPlus, Ban, Clock3 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["all", "pending", "resolved"];

export const MigrationReviewPanel = ({ batch, applying, onApply }) => {
  const [payload, setPayload] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!batch?.id) return;
    const { data } = await api.get(`/admin/import/batches/${batch.id}/identity-reviews`);
    setPayload(data);
  }, [batch?.id]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const rows = useMemo(() => (payload?.rows || []).filter((row) => {
    if (filter !== "all" && row.status !== filter) return false;
    return !search || `${row.display_name} ${row.review_type}`.toLowerCase().includes(search.toLowerCase());
  }), [payload, filter, search]);

  const decide = async (row, action) => {
    setBusy(`${row.id}:${action}`);
    try {
      const target = targets[row.id] || row.candidates?.[0]?.id;
      await api.post(`/admin/import/batches/${batch.id}/identity-reviews/${row.id}/decision`, {
        action,
        target_student_id: action === "link_existing" && row.entity_type === "student" ? target : null,
        target_parent_id: action === "link_existing" && row.entity_type !== "student" ? target : null,
      });
      toast.success(action === "defer" ? "Review kept pending" : "Identity decision saved");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  if (!payload) return <div className="h-28 animate-pulse rounded-lg bg-muted" data-testid="migration-review-loading" />;
  const ready = payload.readiness;
  const resolved = ready.summary?.resolved || 0;

  return (
    <section className="mb-6 space-y-4" data-testid="migration-review-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["Records", ready.total, "migration-kpi-total-records"],
          ["Resolved", resolved, "migration-kpi-valid-count"],
          ["Needs action", ready.pending, "migration-kpi-warning-count"],
          ["Blockers", ready.summary?.severity_blocker || 0, "migration-kpi-conflict-count"],
        ].map(([label, value, id]) => (
          <div key={label} className="border-l-4 border-l-[color:var(--v2-primary)] bg-white border-y border-r rounded-r-md p-4" data-testid={id}>
            <p className="text-xs font-semibold text-muted-foreground">{label}</p>
            <p className="text-2xl font-display font-semibold mt-1">{value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex gap-2 overflow-x-auto">
          {FILTERS.map((item) => <Button key={item} size="sm" variant={filter === item ? "default" : "outline"}
            onClick={() => setFilter(item)} data-testid={`migration-filter-tab-${item}`}>
            {item === "all" ? "All" : item === "pending" ? "Needs action" : "Resolved"}
          </Button>)}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search identity reviews"
          className="h-9 w-full md:w-72 rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          data-testid="migration-search-input" />
      </div>

      <div className="border bg-white rounded-lg overflow-hidden">
        {!rows.length ? <p className="p-8 text-center text-sm text-muted-foreground" data-testid="migration-review-empty">No reviews in this view.</p> :
          rows.map((row) => (
            <div key={row.id} className="grid lg:grid-cols-[1.2fr_1fr_auto] gap-4 p-4 border-b last:border-0" data-testid={`migration-candidate-row-${row.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold truncate">{row.display_name}</p>
                  <Badge variant={row.status === "resolved" ? "secondary" : "outline"} data-testid={`migration-row-status-badge-${row.id}`}>
                    {row.status}
                  </Badge>
                  {row.severity === "blocker" && <Badge variant="destructive">Blocker</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{row.review_type.replaceAll("_", " ")}</p>
                <p className="text-xs text-muted-foreground mt-2 break-words">
                  {row.evidence?.student_names?.join(" · ") || row.evidence?.guardian_contacts?.map((c) => c.email).join(" · ") || "No guardian contact supplied"}
                </p>
              </div>
              <div>
                {row.candidates?.length > 0 ? (
                  <select value={targets[row.id] || row.selected_student_id || row.selected_parent_id || row.candidates[0].id}
                    onChange={(e) => setTargets((old) => ({ ...old, [row.id]: e.target.value }))}
                    className="h-9 w-full rounded-md border bg-white px-2 text-sm"
                    data-testid={`migration-candidate-select-${row.id}`}>
                    {row.candidates.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.full_name} · {candidate.student_code || candidate.email || candidate.id}</option>)}
                  </select>
                ) : <p className="text-xs text-muted-foreground">No existing profile candidate</p>}
              </div>
              <div className="flex flex-wrap lg:justify-end gap-2">
                {row.candidates?.length > 0 && <Button size="sm" variant="outline" disabled={!!busy}
                  onClick={() => decide(row, "link_existing")} data-testid={`migration-action-link-existing-button-${row.id}`}>
                  <Link2 className="h-3.5 w-3.5 mr-1" /> Link
                </Button>}
                <Button size="sm" variant="outline" disabled={!!busy || row.review_type === "student_identifier_conflict"}
                  onClick={() => decide(row, "create_unclaimed")} data-testid={`migration-action-create-unclaimed-button-${row.id}`}>
                  <UserPlus className="h-3.5 w-3.5 mr-1" /> New
                </Button>
                <Button size="sm" variant="ghost" disabled={!!busy}
                  onClick={() => decide(row, "ignore")} data-testid={`migration-action-ignore-button-${row.id}`} title="Ignore this imported identity">
                  <Ban className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" disabled={!!busy}
                  onClick={() => decide(row, "defer")} data-testid={`migration-action-defer-button-${row.id}`} title="Keep pending">
                  <Clock3 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
      </div>

      <div className={`border-l-4 rounded-r-lg p-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between ${ready.ready ? "border-l-emerald-600 bg-emerald-50" : "border-l-amber-600 bg-amber-50"}`}
        data-testid="migration-batch-readiness-gate-card">
        <div className="flex gap-3">
          {ready.ready ? <CheckCircle2 className="h-5 w-5 text-emerald-700" /> : <AlertTriangle className="h-5 w-5 text-amber-700" />}
          <div><p className="font-semibold">{ready.ready ? "Identity-safe and ready" : "Review required"}</p><p className="text-sm text-muted-foreground">{ready.message}</p></div>
        </div>
        {batch.status === "preview" && <Button disabled={!ready.ready || applying} onClick={onApply} data-testid="migration-commit-batch-button">
          {applying ? "Importing…" : "Apply reviewed import"}
        </Button>}
      </div>
    </section>
  );
};